import React, { useEffect, useState } from "react";
import { Card, Row, Col } from "react-bootstrap";
import Chart from "react-apexcharts";
import { useGetLeadDistributionStatsMutation } from "../../api/usersAPISlices";
import { formatNumber, formatPercentage } from "../../utils/formatters";

const LeadDistributionChart = ({ startDate, endDate }) => {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [getLeadDistributionStats] = useGetLeadDistributionStatsMutation();
  
  const token = JSON.parse(localStorage.getItem("token"));

  useEffect(() => {
    if (startDate && endDate) {
      fetchStats();
    }
  }, [startDate, endDate]);

  const fetchStats = async () => {
    setIsLoading(true);
    try {
      const res = await getLeadDistributionStats({ 
        token, 
        startDate, 
        endDate 
      }).unwrap();
      setStats(res);
    } catch (error) {
      console.error("Error fetching lead distribution stats:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Early return if loading or no stats
  if (isLoading || !stats) {
    return (
      <Card style={{ borderRadius: '12px', border: '1px solid #e5e5e5' }}>
        <Card.Body>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  // Check if we have any data at all to display
  const hasAnyData = stats && (
    (stats.leads_by_source && Array.isArray(stats.leads_by_source) && stats.leads_by_source.length > 0) ||
    (stats.distribution_by_user && Array.isArray(stats.distribution_by_user) && stats.distribution_by_user.length > 0) ||
    (stats.daily_distribution && Array.isArray(stats.daily_distribution) && stats.daily_distribution.length > 0)
  );

  // Horizontal bar chart for source breakdown - handles empty data better
  const hasSourceData = stats.leads_by_source && Array.isArray(stats.leads_by_source) && stats.leads_by_source.length > 0;
  
  // Create a simple horizontal bar chart for leads by source
  const sourceChartOptions = {
    chart: {
      type: 'bar',
      height: 300,
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      }
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '60%',
        distributed: true,
        dataLabels: {
          position: 'top'
        }
      }
    },
    colors: ['#3378FF', '#28a745', '#ffc107', '#dc3545', '#17a2b8'],
    dataLabels: {
      enabled: true,
      textAnchor: 'start',
      style: {
        colors: ['#fff']
      },
      formatter: function(val) {
        return formatNumber(val) + " leads";
      },
      offsetX: 0,
      dropShadow: {
        enabled: false
      }
    },
    xaxis: {
      categories: hasSourceData ? stats.leads_by_source.map(s => s.name || 'Unknown') : ['No Data'],
      labels: {
        style: {
          colors: '#9ca3af',
          fontSize: '12px'
        }
      }
    },
    yaxis: {
      labels: {
        style: {
          colors: '#374151',
          fontSize: '13px',
          fontWeight: 500
        }
      }
    },
    tooltip: {
      y: {
        formatter: function(value) {
          return formatNumber(value) + ' leads';
        }
      }
    },
    legend: {
      show: false
    }
  };

  const sourceChartSeries = [{
    name: 'Leads',
    data: hasSourceData ? stats.leads_by_source.map(s => s.count || 0) : [0]
  }];

  // Bar chart configuration for user distribution
  const hasUserData = stats.distribution_by_user && Array.isArray(stats.distribution_by_user) && stats.distribution_by_user.length > 0;
  
  const barChartOptions = {};
  const barChartSeries = [];
  
  if (hasUserData) {
    Object.assign(barChartOptions, {
    chart: {
      type: 'bar',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      }
    },
    colors: ['#3378FF'],
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 6,
        barHeight: '60%',
        dataLabels: {
          position: 'top'
        }
      }
    },
    dataLabels: {
      enabled: true,
      offsetX: -6,
      style: {
        fontSize: '12px',
        colors: ['#fff']
      },
      formatter: function(val) {
        return formatNumber(parseInt(val));
      }
    },
    grid: {
      borderColor: '#f1f1f1',
      strokeDashArray: 4,
      xaxis: {
        lines: {
          show: true
        }
      },
      yaxis: {
        lines: {
          show: false
        }
      },
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
      }
    },
    xaxis: {
      categories: hasUserData ? stats.distribution_by_user.slice(0, 10).map(u => u.name) : [],
      labels: {
        style: {
          colors: '#9ca3af',
          fontSize: '12px'
        }
      },
      axisBorder: {
        show: false
      },
      axisTicks: {
        show: false
      }
    },
    yaxis: {
      labels: {
        style: {
          colors: '#374151',
          fontSize: '12px'
        }
      }
    },
    tooltip: {
      y: {
        formatter: function(value) {
          return formatNumber(value) + ' leads assigned';
        }
      }
    }
    });
    barChartSeries.push({
      name: 'Leads Assigned',
      data: stats.distribution_by_user ? stats.distribution_by_user.slice(0, 10).map(u => u.count || 0) : []
    });
    // Ensure we have at least some data for ApexCharts to render
    if (barChartSeries[0].data.length === 0) {
      barChartSeries[0].data = [0];
      barChartOptions.xaxis.categories = ['No Data'];
    }
  }

  // Line chart configuration for distribution rate over time
  const hasDailyData = stats.daily_distribution && Array.isArray(stats.daily_distribution) && stats.daily_distribution.length > 0;
  
  const lineChartOptions = {};
  const lineChartSeries = [];
  
  if (hasDailyData) {
    Object.assign(lineChartOptions, {
    chart: {
      type: 'area',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      },
      zoom: {
        enabled: false
      },
      stacked: false
    },
    colors: ['#3378FF', '#28a745'],
    stroke: {
      curve: 'smooth',
      width: 2
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.5,
        gradientToColors: ['#66a6ff', '#5cb85c'],
        inverseColors: false,
        opacityFrom: 0.3,
        opacityTo: 0.05,
        stops: [0, 100]
      }
    },
    dataLabels: {
      enabled: false
    },
    grid: {
      borderColor: '#f1f1f1',
      strokeDashArray: 4,
      xaxis: {
        lines: {
          show: false
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      },
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
      }
    },
    xaxis: {
      categories: hasDailyData ? stats.daily_distribution.map(d => d.date) : [],
      labels: {
        style: {
          colors: '#9ca3af',
          fontSize: '12px'
        },
        rotate: -45,
        rotateAlways: false
      },
      axisBorder: {
        show: false
      },
      axisTicks: {
        show: false
      }
    },
    yaxis: {
      title: {
        text: 'Number of Leads',
        style: {
          color: '#374151',
          fontSize: '13px',
          fontWeight: 500
        }
      },
      labels: {
        style: {
          colors: '#374151',
          fontSize: '12px'
        },
        formatter: function(val) {
          return formatNumber(Math.round(val));
        }
      },
      min: 0
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      floating: true,
      offsetY: -10,
      offsetX: -5,
      fontSize: '13px',
      fontWeight: 400,
      labels: {
        colors: '#374151'
      },
      markers: {
        width: 12,
        height: 12,
        strokeWidth: 0,
        radius: 12,
      },
      itemMargin: {
        horizontal: 10,
        vertical: 0
      }
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: function(value) {
          return `${formatNumber(value)} leads`;
        }
      }
    }
    });
    lineChartSeries.push(
      {
        name: 'Recorded',
        data: stats.daily_distribution ? stats.daily_distribution.map(d => d.recorded || 0) : []
      },
      {
        name: 'Distributed', 
        data: stats.daily_distribution ? stats.daily_distribution.map(d => d.distributed || 0) : []
      }
    );
    // Ensure we have at least some data for ApexCharts to render
    if (lineChartSeries[0].data.length === 0) {
      lineChartSeries[0].data = [0];
      lineChartSeries[1].data = [0];
      lineChartOptions.xaxis.categories = ['No Data'];
    }
  }

  // If no data available for the selected period, show a simple message
  if (!hasAnyData && stats) {
    return (
      <>
        <Row className="mb-4">
          <Col>
            <h5 className="font-weight-bold mb-3">Lead Distribution Statistics</h5>
          </Col>
        </Row>
        
        <Row>
          <Col>
            <Card style={{ borderRadius: '15px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}>
              <Card.Body>
                <div className="text-center py-5">
                  <p className="text-muted">No lead distribution data available for the selected period.</p>
                  <p className="text-muted small">Try selecting a different date range.</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </>
    );
  }

  // Render the dashboard
  return (
    <>
      <Row className="mb-4">
        <Col>
          <h5 className="font-weight-bold mb-3">Lead Distribution Statistics</h5>
        </Col>
      </Row>
      
      {/* Summary Cards */}
      <Row className="g-3 mb-4">
        <Col md="4">
          <Card className="h-100" style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.06)', transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}>
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <p className="mb-1 text-muted small" style={{fontSize: '12px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px'}}>{stats?.source_1_name || 'Source 1'} - Recorded</p>
                  <h3 className="mb-0 font-weight-bold" style={{color: '#1a202c', fontSize: '28px'}}>{formatNumber(stats?.total_recorded || 0)}</h3>
                </div>
                <div className="rounded-circle p-3" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'}}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 11H15" stroke="#ffffff" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M12 8V14" stroke="#ffffff" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="12" cy="12" r="9" stroke="#ffffff" strokeWidth="2"/>
                  </svg>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md="4">
          <Card className="h-100" style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.06)', transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}>
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <p className="mb-1 text-muted small" style={{fontSize: '12px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px'}}>{stats?.source_1_name || 'Source 1'} - Distributed</p>
                  <h3 className="mb-0 font-weight-bold" style={{color: '#10b981', fontSize: '28px'}}>{formatNumber(stats?.total_distributed || 0)}</h3>
                </div>
                <div className="rounded-circle p-3" style={{background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)'}}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 12L10 15L17 8" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="9" stroke="#ffffff" strokeWidth="2"/>
                  </svg>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md="4">
          <Card className="h-100" style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.06)', transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}>
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <p className="mb-1 text-muted small" style={{fontSize: '12px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px'}}>{stats?.source_1_name || 'Source 1'} - Unassigned</p>
                  <h3 className="mb-0 font-weight-bold" style={{color: '#f59e0b', fontSize: '28px'}}>{formatNumber(stats?.unassigned || 0)}</h3>
                </div>
                <div className="rounded-circle p-3" style={{background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)'}}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 8V12" stroke="#ffffff" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="12" cy="16" r="1" fill="#ffffff"/>
                    <circle cx="12" cy="12" r="9" stroke="#ffffff" strokeWidth="2"/>
                  </svg>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts Row */}
      <Row className="g-3">
        <Col lg="5">
          <Card style={{ borderRadius: '12px', border: '1px solid #e5e5e5' }}>
            <Card.Body>
              <h6 className="font-weight-bold mb-3">Leads by Source</h6>
              <Chart
                options={sourceChartOptions}
                series={sourceChartSeries}
                type="bar"
                height="300"
              />
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg="7">
          <Card style={{ borderRadius: '12px', border: '1px solid #e5e5e5' }}>
            <Card.Body>
              <h6 className="font-weight-bold mb-3">Top 10 Users by Lead Count</h6>
              {hasUserData && Object.keys(barChartOptions).length > 0 && barChartSeries.length > 0 && barChartSeries[0]?.data?.length > 0 ? (
                <Chart
                  options={barChartOptions}
                  series={barChartSeries}
                  type="bar"
                  height="300"
                />
              ) : (
                <div className="text-center text-muted py-5">
                  <p>No user distribution data available</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Distribution Rate and Percentage Charts Side by Side */}
      <Row className="g-3 mt-3">
        <Col lg="6">
          <Card style={{ borderRadius: '12px', border: '1px solid #e5e5e5', height: '100%' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="font-weight-bold mb-0">Distribution Rate Over Time</h6>
                <div className="d-flex gap-2">
                  <span className="badge bg-primary bg-opacity-10 text-primary d-flex align-items-center gap-1" style={{ padding: '4px 8px', fontSize: '11px' }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#3378FF' }}></span>
                    Recorded
                  </span>
                  <span className="badge bg-success bg-opacity-10 text-success d-flex align-items-center gap-1" style={{ padding: '4px 8px', fontSize: '11px' }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#28a745' }}></span>
                    Distributed
                  </span>
                </div>
              </div>
              {hasDailyData && Object.keys(lineChartOptions).length > 0 && lineChartSeries.length > 0 && lineChartSeries[0]?.data?.length > 0 ? (
                <Chart
                  options={lineChartOptions}
                  series={lineChartSeries}
                  type="area"
                  height="300"
                />
              ) : (
                <div className="text-center text-muted py-5">
                  <p>No daily distribution data available</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg="6">
          <Card style={{ borderRadius: '12px', border: '1px solid #e5e5e5', height: '100%' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="font-weight-bold mb-0">Distribution Percentage Over Time</h6>
                <div className="d-flex align-items-center">
                  <span className="text-muted small">% of leads distributed daily</span>
                </div>
              </div>
              {hasDailyData && stats.daily_distribution ? (
                <Chart
                  options={{
                    chart: {
                      type: 'line',
                      fontFamily: 'Inter, sans-serif',
                      toolbar: {
                        show: false
                      },
                      zoom: {
                        enabled: false
                      }
                    },
                    colors: ['#10b981'],
                    stroke: {
                      curve: 'smooth',
                      width: 3
                    },
                    fill: {
                      type: 'gradient',
                      gradient: {
                        shade: 'light',
                        type: 'vertical',
                        shadeIntensity: 0.5,
                        gradientToColors: ['#34d399'],
                        inverseColors: false,
                        opacityFrom: 0.4,
                        opacityTo: 0.1,
                        stops: [0, 100]
                      }
                    },
                    dataLabels: {
                      enabled: false
                    },
                    grid: {
                      borderColor: '#f1f1f1',
                      strokeDashArray: 4,
                      xaxis: {
                        lines: {
                          show: false
                        }
                      },
                      yaxis: {
                        lines: {
                          show: true
                        }
                      },
                      padding: {
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0
                      }
                    },
                    xaxis: {
                      categories: stats.daily_distribution.map(d => d.date),
                      labels: {
                        style: {
                          colors: '#9ca3af',
                          fontSize: '12px'
                        },
                        rotate: -45,
                        rotateAlways: false
                      },
                      axisBorder: {
                        show: false
                      },
                      axisTicks: {
                        show: false
                      }
                    },
                    yaxis: {
                      title: {
                        text: 'Distribution Rate (%)',
                        style: {
                          color: '#374151',
                          fontSize: '13px',
                          fontWeight: 500
                        }
                      },
                      labels: {
                        style: {
                          colors: '#374151',
                          fontSize: '12px'
                        },
                        formatter: function(val) {
                          return val.toFixed(0) + '%';
                        }
                      },
                      min: Math.floor(Math.min(...stats.daily_distribution.map(d => d.rate || 0)) * 0.9), // 90% of minimum value for padding
                      max: 100
                    },
                    markers: {
                      size: 0,
                      hover: {
                        size: 6
                      }
                    },
                    tooltip: {
                      y: {
                        formatter: function(value) {
                          return `${value.toFixed(1)}%`;
                        },
                        title: {
                          formatter: function() {
                            return 'Distribution Rate';
                          }
                        }
                      },
                      custom: function({ dataPointIndex }) {
                        const data = stats.daily_distribution[dataPointIndex];
                        return '<div class="px-2 py-1">' +
                          '<div class="fw-bold">' + data.date + '</div>' +
                          '<div class="small">Recorded: ' + data.recorded + '</div>' +
                          '<div class="small">Distributed: ' + data.distributed + '</div>' +
                          '<div class="small text-success">Rate: ' + data.rate + '%</div>' +
                          '</div>';
                      }
                    }
                  }}
                  series={[{
                    name: 'Distribution Rate',
                    data: stats.daily_distribution.map(d => d.rate || 0)
                  }]}
                  type="area"
                  height="300"
                />
              ) : (
                <div className="text-center text-muted py-5">
                  <p>No distribution percentage data available</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default LeadDistributionChart;